import { TnplAcceptableRiskLevel } from "@redotech/redo-tnpl-settings-common/tnpl-settings";
import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

export enum TnplContractStatus {
  TRIAL_ACTIVE = "trial_active",
  CANCELLED = "cancelled",
  COMPLETE_PAID_BY_CUSTOMER = "complete_paid_by_customer",
  COMPLETE_RETURNED = "complete_returned",
}

export enum ShippingDueTrigger {
  ORDER_PURCHASE = "order_purchase",
  TRIAL_END = "trial_end",
}

export enum TnplChargeType {
  AUTHORIZATION = "authorization",
  CAPTURE_PREVIOUS_AUTHORIZATION = "capture_previous_authorization",
  AUTHORIZE_AND_CAPTURE = "authorize_and_capture",
}

export enum TnplChargeStatus {
  PENDING = "pending",
  SUCCEEDED = "succeeded",
  FAILED = "failed",
  API_FAILURE = "api_failure",
}

export enum TnplCancellationType {
  INITIAL_PAYMENT_FAILED = "initial_payment_failed",
  INELIGIBLE_FOR_TRIAL = "ineligible_for_trial",
}

export enum TnplOrderIneligibleReasonType {
  FRAUD_RISK = "fraud_risk",
  CART_VALUE_EXCEEDED = "cart_value_exceeded",
  CART_ITEM_COUNT_EXCEEDED = "cart_item_count_exceeded",
}

// Zod schemas for each ineligible reason type
const FraudRiskReasonSchema = z.object({
  type: z.literal(TnplOrderIneligibleReasonType.FRAUD_RISK),
  highestRiskLevel: createEnum(TnplAcceptableRiskLevel, {
    enumName: "TnplAcceptableRiskLevel",
  }),
  acceptableRiskLevel: createEnum(TnplAcceptableRiskLevel, {
    enumName: "TnplAcceptableRiskLevel",
  }),
});

const CartValueExceededReasonSchema = z.object({
  type: z.literal(TnplOrderIneligibleReasonType.CART_VALUE_EXCEEDED),
  cartValue: z.number(),
  cartValueLimit: z.number(),
});

const CartItemCountExceededReasonSchema = z.object({
  type: z.literal(TnplOrderIneligibleReasonType.CART_ITEM_COUNT_EXCEEDED),
  cartTnplItemCount: z.number(),
  cartMaxTnplItems: z.number(),
});

// Union schema for all ineligible reason types
export const TnplOrderIneligibleReasonSchema = z.discriminatedUnion("type", [
  FraudRiskReasonSchema,
  CartValueExceededReasonSchema,
  CartItemCountExceededReasonSchema,
]);

// TypeScript type derived from the Zod schema
export type TnplOrderIneligibleReason = z.infer<
  typeof TnplOrderIneligibleReasonSchema
>;

export const TnplChargeSchema = z.object({
  idempotencyKey: z.string(),
  paymentReferenceId: z.string(),
  transactionId: z.string().optional(),
  parentTransactionId: z.string().optional(),
  date: z.date(),
  type: createEnum(TnplChargeType, { enumName: "TnplChargeType" }),
  status: createEnum(TnplChargeStatus, { enumName: "TnplChargeStatus" }),
  amount: z.number().optional(),
  jobId: z.string().optional(),
});

export type TnplCharge = z.infer<typeof TnplChargeSchema>;

export const TnplContractSchema = z.object({
  _id: zExt.objectId(),
  team: zExt.objectId(),
  tnplContractTemplate: zExt.objectId(),
  shopifyOrderId: z.number(),
  shopifyLineItemIds: z.array(z.number()),
  shopifySellingPlanId: z.number(),
  shopifyPaymentScheduleIds: z.array(z.number()).optional(),
  shopifyPaymentMandateIds: z.array(z.string()),
  initialSellingPlanAmount: z.number(),
  remainingTrialAmount: z.number().optional(),
  sellingPlanCurrency: z.string(),
  order: zExt.objectId(),
  return: zExt.objectId().optional(),
  orderDate: z.date(),
  initialPaymentDate: z.date().optional(),
  deliveryDate: z.date().optional(),
  returnedDate: z.date().optional(),
  approvedDate: z.date().optional(),
  trialPeriodEnd: z.date().optional(),
  chargeDate: z.date().optional(),
  merchantProcessingEndDate: z.date().optional(),
  trialDatesCommitted: z.boolean().optional().default(false),
  status: createEnum(TnplContractStatus, { enumName: "TnplContractStatus" }),
  charges: z.array(TnplChargeSchema),
  orderRiskLevel: z.string().optional(),
  orderIneligibleReason: TnplOrderIneligibleReasonSchema.optional(),
  cancelledReason: createEnum(TnplCancellationType, {
    enumName: "TnplCancellationType",
  }).optional(),
  defaultedAt: z.date().optional(),
  shippingDueLater: z.number().nullish(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export type TnplContract = z.infer<typeof TnplContractSchema>;
export interface ITnplContract extends z.ZodType<TnplContract> {}

export const MERCHANT_PROCESSING_TIME_DAYS = 10; // Delay payment capture by 10 days when return is pending

/**
 * Status display mapping for merchant-friendly TNPL contract status labels
 */
const TNPL_STATUS_DISPLAY_MAP: Record<TnplContractStatus, string> = {
  [TnplContractStatus.TRIAL_ACTIVE]: "Active",
  [TnplContractStatus.CANCELLED]: "Cancelled",
  [TnplContractStatus.COMPLETE_PAID_BY_CUSTOMER]: "Paid",
  [TnplContractStatus.COMPLETE_RETURNED]: "Returned",
};

/**
 * Formats a TNPL contract status for merchant display
 * @param status - The raw TNPL contract status
 * @returns A formatted, merchant-friendly status string
 */
export function formatTnplContractStatus(status: string): string {
  const statusEnum = status as TnplContractStatus;
  return (
    TNPL_STATUS_DISPLAY_MAP[statusEnum] ||
    status
      .replace(/_/g, " ")
      .toLowerCase()
      .replace(/\b\w/g, (l: string) => l.toUpperCase())
  );
}
